<script lang="ts">
  import { getContext, onMount } from "svelte";
  import { savedCards } from "../../../../../app/store/store-auth";
  import { CREATE_NEW_ACCOUNT_CONTEXT_NAME } from "../../utils";
  import type { CreateNewAccountContext, PaymentMethod } from "../../type";
  import Button from "../../../../common/form/Button.svelte";
  import Icon from "../../../../common/icons/Icon.svelte";
  import FormInput from "../../../../common/form/FormInputWithSurroundError.svelte";
  import Checkbox from "../../../../common/form/Checkbox.svelte";
  import CardPaymentForm from "./CardPaymentForm.svelte";

  const context: CreateNewAccountContext = getContext(
    CREATE_NEW_ACCOUNT_CONTEXT_NAME
  );
  const totalCost = context.totalCost;
  let emailError: string = "";
  let paymentMethod: PaymentMethod = "creditcard";
  let savedCard: string = "";
  let isSubmiting: boolean = false;
  let handleStripePayment: () => Promise<any>;

  function changePaymentMethod(method: PaymentMethod) {
    paymentMethod = method;
    if (paymentMethod !== "savedcard") savedCard = "";
  }

  function getCardBG(index: number) {
    let bgIcon: string;
    if (index % 2 === 0)
      bgIcon = `
        <svg width="78" height="52" viewBox="0 0 78 52" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="78" height="52" rx="6" fill="url(#paint0_linear_3258_61079)"/>
          <defs>
            <linearGradient id="paint0_linear_3258_61079" x1="0" y1="0" x2="78" y2="52" gradientUnits="userSpaceOnUse">
              <stop stop-color="#6D777E"/>
              <stop offset="1" stop-color="#454450"/>
            </linearGradient>
          </defs>
        </svg>
      `;
    else
      bgIcon = `
        <svg width="78" height="52" viewBox="0 0 78 52" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="78" height="52" rx="6" fill="url(#paint0_linear_3709_61100)"/>
          <defs>
            <linearGradient id="paint0_linear_3709_61100" x1="0" y1="0" x2="78" y2="52" gradientUnits="userSpaceOnUse">
              <stop stop-color="#454450"/>
              <stop offset="1" stop-color="#0E0121"/>
            </linearGradient>
          </defs>
        </svg>
      `;
    return bgIcon;
  }

  async function handleFormSubmit() {
    if (isSubmiting) return;
    isSubmiting = true;
    if (paymentMethod === "creditcard") await handleStripePayment();
    isSubmiting = false;
  }

  $: cost = $totalCost;
  $: if (savedCard) changePaymentMethod("savedcard");
  $: useCreditCard = paymentMethod === "creditcard";
  $: usePaypal = paymentMethod === "paypal";
</script>

<div>
  <form
    method="POST"
    on:submit|preventDefault|stopPropagation={handleFormSubmit}
  >
    <div class="relative flex flex-row space-x-3 mt-8" role="radiogroup">
      {#each savedCards as card, index (card.id)}
        {@const selected = savedCard === card.id}

        <label>
          <input
            bind:group={savedCard}
            class="absolute top-0 left-0 w-0 h-0 opacity-0"
            type="radio"
            value={card.id}
            name="savedCard"
          />
          <div class="saved-card-container" class:selected>
            <div class="card">
              {@html getCardBG(index)}
              <div class="absolute top-2 left-2">
                <Icon icon="master-card-logo" width="20px" height="12px" />
              </div>
              <div
                class="absolute left-2 bottom-1 text-[10px] font-semibold text-white tracking-[0.04em]"
              >
                ** {card.number}
              </div>
            </div>
          </div>
        </label>
      {/each}
    </div>

    <div class="payment-method-container mt-7" class:active={useCreditCard}>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class="relative flex flex-row space-x-3 pl-4 py-2.5 rounded cursor-pointer"
        class:bg-light-50={useCreditCard}
        on:click={() => changePaymentMethod("creditcard")}
      >
        <Icon icon="credit-card" size="20px" />
        <span>Credit / Debit Card</span>

        <div
          class="absolute top-0 bottom-0 right-4 flex flex-row items-center"
          class:rotate-180={useCreditCard}
        >
          <Icon icon="arrow-down" width="12px" height="6px" />
        </div>
      </div>

      <div class:hidden={!useCreditCard}>
        <div class="px-6 pt-6">
          <CardPaymentForm bind:handleStripePayment />
        </div>

        <div class="px-6 mb-6">
          <Checkbox
            name="remember"
            description="Saved this Card for the future payments."
            labelStyle="font-size: 11px; color: #454450; font-weight: normal;"
          />
        </div>
      </div>
    </div>

    <div class="payment-method-container mt-3" class:active={usePaypal}>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class="relative flex flex-row space-x-3 pl-4 py-2.5 rounded cursor-pointer"
        class:bg-light-50={usePaypal}
        on:click={() => changePaymentMethod("paypal")}
      >
        <Icon icon="paypal" size="20px" />
        <span>Paypal</span>

        <div
          class="absolute top-0 bottom-0 right-4 flex flex-row items-center"
          class:rotate-180={usePaypal}
        >
          <Icon icon="arrow-down" width="12px" height="6px" />
        </div>
      </div>

      <div class="px-6 pt-2 pb-6" class:hidden={!usePaypal}>
        <div class="input-field">
          <p class="required">Email Address</p>
          <div class="input-container">
            <div class="prefix-icon">
              <Icon icon="input-email" width="20px" height="18px" />
            </div>

            <FormInput
              name="email"
              errorIndicatorIconSize="20px"
              errorMessage={emailError}
              showErrorIndicatorIcon={true}
              showErrorMessage={false}
              type="email"
              spellcheck="false"
              placeholder="Enter Your Email"
              autocomplete="off"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="control-buttons">
      <div class="w-1 flex-shrink flex-grow">
        <Button
          class="w-full px-1"
          color="secondary"
          type="button"
          on:click={context.onPrevious}
        >
          Cancel
        </Button>
      </div>

      <div class="w-1 flex-shrink flex-grow">
        <Button class="w-full px-1">
          <div class="flex flex-row items-center justify-center space-x-1.5">
            <Icon icon="payment-lock" size="17px" />
            <span>Pay ${cost} Now </span>
          </div>
        </Button>
      </div>
    </div>
  </form>
</div>

<style lang="postcss">
  /* saved card */
  .saved-card-container {
    @apply relative z-20;
    padding: 3px;
    /* border-2 border-solid border-primary-datadocs-blue rounded-lg */
  }

  .saved-card-container.selected::before {
    @apply absolute top-0 bottom-0 left-0 right-0 rounded-lg z-0;
    content: "";
    background: linear-gradient(115.49deg, #3bc7ff 0%, #5f89ff 100%);
  }

  .saved-card-container.selected::after {
    @apply absolute top-0.5 bottom-0.5 left-0.5 right-0.5 rounded-md z-10 bg-white;
    content: "";
  }

  .card {
    @apply relative cursor-pointer rounded-md z-20;
    width: 78px;
    height: 52px;
  }

  .saved-card-container.selected .card,
  .card:hover {
    box-shadow: 0px 4px 12px rgba(14, 1, 33, 0.33);
  }

  /* payment method */
  .payment-method-container {
    @apply w-full p-0.5 rounded border border-solid border-light-100;
  }

  .payment-method-container.active {
    @apply border-transparent;
    box-shadow: 1px 2px 12px rgba(55, 84, 170, 0.12);
  }

  .input-field {
    margin-top: 0px !important;
  }

  .control-buttons {
    @apply flex flex-row mt-24 space-x-3;
  }

  .control-buttons :global(button) {
    @apply px-1;
  }

  @media (max-width: 450px) {
    .control-buttons {
      @apply flex-col space-x-0 space-y-2;
    }

    .control-buttons > div {
      @apply w-full;
    }
  }
</style>
